/*
 * Button
 */
import { Slot } from "@radix-ui/react-slot"
import { cn } from "@timeprox/react/helpers"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean
	}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button"
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		)
	},
)
Button.displayName = "Button"

export { Button }

/* Variants */
export const buttonVariants = cva(
	"ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center cursor-pointer whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground hover:bg-primary/90 transition-colors",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors",
				alternate:
					"bg-alternate hover:bg-alternate/90 text-alternate-foreground transition-colors",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors",
				outline:
					"border-input bg-background hover:bg-accent hover:text-accent-foreground border transition-colors",
				ghost: "hover:bg-accent hover:text-accent-foreground transition-colors",
				link: "text-primary underline-offset-4 transition-colors hover:underline",
				field: "border-input bg-field border",
				none: "",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				xl: "text-md h-14 rounded-md px-14",
				icon: "size-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
)
